import produce from 'immer';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import type { WatchQueryFetchPolicy } from '@cyren/common-lib';
import { AppStateCookie } from '../../app/use-app-state';
import { PaDataRepoState } from '../../report/use-data';
import type { PaDataRepoStateT } from '../../patient/patient-types';
import { translationControllerSearch } from '../api/generated/translation/translation';
import type {
  SearchTranslationResponseDto,
  TranslationResponseDto,
} from '../api/generated/types';

type UsePatientTransDataOptions = {
  locale?: string;
  keys?: string[];
};

type FetchTransOptions = {
  fetchPolicy?: WatchQueryFetchPolicy;
  asNeededKeys?: string[];
};

type StateTranslation = PaDataRepoStateT['translations'][number];
type StateTranslations = PaDataRepoStateT['translations'];

const inFlightSearchRequests = new Map<
  string,
  Promise<SearchTranslationResponseDto | undefined>
>();

const normalizeKeys = (keys?: string[]) => {
  const raw = keys || [];
  const normalized = raw
    .map((key) => (typeof key === 'string' ? key.trim() : ''))
    .filter((key): key is string => key.length > 0);
  return Array.from(new Set(normalized)).sort();
};

const buildSearchSignature = ({
  surveyId,
  locale,
  keys,
}: {
  surveyId: number;
  locale: string;
  keys: string[];
}) => `${surveyId}:${locale}:${keys.join('|')}`;

const toStateTranslation = (
  item: TranslationResponseDto,
  fallbackLocale: string,
): StateTranslation => ({
  id: item.id != null ? String(item.id) : null,
  key: item.key,
  value: item.value ?? null,
  locale: (item.locale || fallbackLocale || null) as StateTranslation['locale'],
  dataType: (item.dataType || null) as StateTranslation['dataType'],
  type: (item.type || null) as StateTranslation['type'],
});

const buildTranslationIdentity = (
  item: StateTranslation,
) => {
  if (item.id) {
    return `id:${item.id}`;
  }

  return `key:${item.key}|locale:${item.locale || ''}|type:${item.type || ''}|dataType:${item.dataType || ''}`;
};

const isSameStateTranslation = (
  previous: StateTranslation,
  next: StateTranslation,
) =>
  previous.id === next.id &&
  previous.key === next.key &&
  previous.value === next.value &&
  previous.locale === next.locale &&
  previous.dataType === next.dataType &&
  previous.type === next.type;

export function usePatientTransData({
  keys,
  locale,
}: UsePatientTransDataOptions = {}) {
  const [appStateCookie] = useRecoilState(AppStateCookie);
  const [dataState, setDataState] = useRecoilState(PaDataRepoState);
  const [dataTrans, setDataTrans] = useState<SearchTranslationResponseDto | undefined>(undefined);
  const [loadingTrans, setLoadingTrans] = useState(false);
  const keysNotFoundRef = useRef<string[]>([]);
  const lastAutoFetchSignatureRef = useRef<string | null>(null);

  const localeKey = locale || appStateCookie?.localeKey || 'english';
  const { survey, translations } = dataState;

  const keysNotFound = useMemo(() => {
    const sourceKeys = normalizeKeys(keys);
    if (sourceKeys.length === 0) return [];

    return sourceKeys
      .filter((key) => {
        const found = translations?.find(
          (translation) => translation.key === key && translation.locale === localeKey,
        );
        return !found;
      })
      .sort();
  }, [keys, localeKey, translations]);

  const keysNotFoundSignature = useMemo(
    () => keysNotFound.join('|'),
    [keysNotFound],
  );

  useEffect(() => {
    keysNotFoundRef.current = keysNotFound;
  }, [keysNotFound]);

  const mergeTranslationsIntoState = useCallback(
    (items?: StateTranslations) => {
      if (!items?.length) return;

      setDataState((state) =>
        produce(state, (draft) => {
          const identityToIndex = new Map<string, number>();
          draft.translations.forEach((translation, index) => {
            identityToIndex.set(buildTranslationIdentity(translation), index);
          });

          items.forEach((item) => {
            const identity = buildTranslationIdentity(item);
            const existingIndex = identityToIndex.get(identity);

            if (existingIndex != null) {
              const previous = draft.translations[existingIndex];
              if (!isSameStateTranslation(previous, item)) {
                draft.translations[existingIndex] = item;
              }
              return;
            }

            draft.translations.push(item);
            identityToIndex.set(identity, draft.translations.length - 1);
          });
        }),
      );
    },
    [setDataState],
  );

  const fetchTrans = useCallback(
    async ({ asNeededKeys }: FetchTransOptions = {}) => {
      const surveyId = Number(survey?.id);
      const hasValidSurveyId = Number.isFinite(surveyId) && surveyId > 0;
      const sourceKeys = isEmpty(asNeededKeys)
        ? keysNotFoundRef.current
        : asNeededKeys || [];
      const keysToFetch = normalizeKeys(sourceKeys);

      if (!hasValidSurveyId || isEmpty(keysToFetch)) {
        return undefined;
      }

      const requestSignature = buildSearchSignature({
        surveyId,
        locale: localeKey,
        keys: keysToFetch,
      });

      const inFlight = inFlightSearchRequests.get(requestSignature);
      if (inFlight) {
        inFlight.then((response) => {
          setDataTrans(response || { translations: [], totalCount: 0 } as SearchTranslationResponseDto);
        });
        return inFlight;
      }

      const request = (async () => {
        try {
          setLoadingTrans(true);

          const response = await translationControllerSearch({
            surveyId,
            locale: localeKey,
            type: 'patient',
            keys: keysToFetch,
            limit: 10000,
          });

          setDataTrans(response);

          const list = (response?.translations || []).map(
            (item: TranslationResponseDto) => toStateTranslation(item, localeKey),
          );
          mergeTranslationsIntoState(list);

          return response;
        } catch (error) {
          console.error('Failed to fetch translations:', error);
          // Set dataTrans to prevent loadingTrans from being stuck true forever
          setDataTrans({ translations: [], totalCount: 0 } as SearchTranslationResponseDto);
          return undefined;
        } finally {
          setLoadingTrans(false);
          inFlightSearchRequests.delete(requestSignature);
        }
      })();

      inFlightSearchRequests.set(requestSignature, request);
      return request;
    },
    [survey?.id, localeKey, mergeTranslationsIntoState, keysNotFoundSignature],
  );

  const autoFetchSignature = useMemo(() => {
    const surveyId = Number(survey?.id);
    const hasValidSurveyId = Number.isFinite(surveyId) && surveyId > 0;

    if (!hasValidSurveyId || keysNotFound.length === 0) {
      return null;
    }

    return buildSearchSignature({
      surveyId,
      locale: localeKey,
      keys: keysNotFound,
    });
  }, [survey?.id, localeKey, keysNotFound, keysNotFoundSignature]);

  useEffect(() => {
    if (!autoFetchSignature) {
      lastAutoFetchSignatureRef.current = null;
      return;
    }

    if (lastAutoFetchSignatureRef.current === autoFetchSignature) {
      return;
    }

    lastAutoFetchSignatureRef.current = autoFetchSignature;
    fetchTrans();
  }, [autoFetchSignature, fetchTrans]);

  // Only report "loading" from the derived check when survey ID is valid
  // (i.e., a fetch can actually happen). Without this guard, pages get stuck
  // in permanent loading when PaDataRepoState.survey is not yet synced.
  const surveyIdForLoading = Number(survey?.id);
  const canAutoFetch = Number.isFinite(surveyIdForLoading) && surveyIdForLoading > 0;
  const derivedLoading = keysNotFound.length > 0 && !dataTrans && canAutoFetch;

  return [
    {
      dataState,
      dataTrans,
      loadingTrans: loadingTrans || derivedLoading,
    },
    {
      fetchTrans,
    },
  ] as const;
}
